/**
 * View: Programs
 * Programs list screen with server-side pagination.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableLoadingRow,
  TableRow,
} from "@/components/ui/table/table";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { formatDate } from "@/utils/Format";
import type {
  ProgramItem,
  ProgramsPaging,
} from "../../domain/model/ProgramsModel";
import { ProgramsViewModel } from "../viewmodel/ProgramsViewModel";

const PAGE_SIZE = 10;

const getStatusBadgeVariant = (
  status: string,
): "success" | "error" | "info" | "warning" => {
  switch (status) {
    case "ongoing":
    case "active":
      return "success";
    case "ended":
      return "error";
    case "not_started":
      return "info";
    case "draft":
      return "warning";
    case "expired":
      return "error";
    default:
      return "info";
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case "ongoing":
      return "Ongoing";
    case "active":
      return "Active";
    case "ended":
      return "Ended";
    case "not_started":
      return "Not Started";
    case "draft":
      return "Draft";
    case "expired":
      return "Expired";
    default:
      return status;
  }
};

export const ProgramsView = (): React.JSX.Element => {
  usePageTitle("Programs");
  
  const navigate = useNavigate();
  const [items, setItems] = useState<ProgramItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paging, setPaging] = useState<ProgramsPaging | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { loadPrograms } = ProgramsViewModel({
    setItems,
    setIsLoading,
    setPaging,
    setError,
  });

  const fetchPage = useCallback(
    (page: number) => {
      void loadPrograms({ page, pageSize: PAGE_SIZE });
    },
    [loadPrograms],
  );

  useEffect(() => {
    fetchPage(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const totalPages = paging?.totalPages ?? 1;

  return (
    <MainLayout title="Programs">
      <div className="flex flex-col gap-4">
        <div className="flex justify-end items-center gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate("/swarapoint/programs/create")}
            >
              + Add New
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Program ID</TableHead>
              <TableHead>Program Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>End Date</TableHead>
              <TableHead> </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableLoadingRow colSpan={6} message="Loading programs..." />
            ) : items.length === 0 ? (
              <TableEmptyRow colSpan={6} message="No programs found." />
            ) : (
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.programId}</TableCell>
                  <TableCell>{item.programName}</TableCell>
                  <TableCell>
                    <StatusBadge variant={getStatusBadgeVariant(item.status)}>
                      {getStatusLabel(item.status)}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>{formatDate(item.startDate)}</TableCell>
                  <TableCell>{formatDate(item.endDate)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        navigate(`/swarapoint/programs/edit/${item.id}`);
                      }}
                      aria-label={`Edit program ${item.programName}`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                        <path d="m15 5 4 4" />
                      </svg>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    </MainLayout>
  );
};
